import React, { useState, useEffect } from 'react';
import { getDatabase, ref, push, serverTimestamp, get, query, orderByChild } from 'firebase/database';
import { useAuth } from '../context/AuthContext';
import './Messaging.css';

const Compose = ({ onMessageSent }) => {
    const { userData } = useAuth();
    const [to, setTo] = useState('');
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [sending, setSending] = useState(false);
    const [status, setStatus] = useState({ message: '', type: '' });
    const db = getDatabase();

    useEffect(() => {
        const usersRef = query(ref(db, 'users'), orderByChild('email'));
        get(usersRef).then((snapshot) => {
            if (snapshot.exists()) {
                const users = [];
                snapshot.forEach(childSnapshot => {
                    users.push({ ...childSnapshot.val(), uid: childSnapshot.key });
                });
                setAllUsers(users);
            }
        });
    }, [db]);

    const handleToChange = (e) => {
        const value = e.target.value;
        setTo(value);
        if (value.length > 2) {
            const filteredSuggestions = allUsers.filter(u =>
                (u.email && u.email.toLowerCase().includes(value.toLowerCase())) ||
                (u.fullName && u.fullName.toLowerCase().includes(value.toLowerCase()))
            ).slice(0, 5);
            setSuggestions(filteredSuggestions);
        } else {
            setSuggestions([]);
        }
    };

    const handleSuggestionClick = (suggestion) => {
        setTo(suggestion.email);
        setSuggestions([]);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!userData || !to) return;

        setSending(true);
        setStatus({ message: '', type: '' });

        const messagesRef = ref(db, 'messages');
        const toUser = allUsers.find(u => u.email === to);

        push(messagesRef, {
            from: userData.email,
            fromName: userData.fullName || userData.username || 'N/A',
            to: to,
            toName: toUser?.fullName || to,
            subject: subject,
            body: body,
            timestamp: serverTimestamp(),
            isRead: false,
        }).then(() => {
            setStatus({ message: 'Message sent successfully!', type: 'success' });
            setTo('');
            setSubject('');
            setBody('');
            setSending(false);
            setTimeout(() => {
                onMessageSent();
            }, 1500); // Wait 1.5 seconds before switching view
        }).catch(error => {
            setStatus({ message: `Failed to send message: ${error.message}`, type: 'error' });
            setSending(false);
        });
    };

    return (
        <div className="compose-view">
            <div className="compose-header">
                <h3>New Message</h3>
            </div>
            {status.message && <div className={`status-message ${status.type}`}>{status.message}</div>}
            <form className="compose-form" onSubmit={handleSubmit}>
                 <div className="compose-form-row to-field">
                    <label htmlFor="to">To</label>
                    <input 
                        id="to"
                        type="text" 
                        value={to} 
                        onChange={handleToChange} 
                        placeholder={(userData?.role === 'Parent' || userData?.role === 'Student') ? "Search for Principal, Admin, or Form Master..." : "Search for a user by name or email..."}
                        required 
                        disabled={sending}
                    />
                    {suggestions.length > 0 && (
                        <ul className="suggestions-list">
                            {suggestions.map((s) => (
                                <li key={s.uid} onClick={() => handleSuggestionClick(s)}>
                                    <strong>{s.fullName}</strong> ({s.role})<br/>
                                    <small>{s.email}</small>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                <div className="compose-form-row">
                    <input 
                        id="subject"
                        type="text" 
                        value={subject} 
                        onChange={(e) => setSubject(e.target.value)} 
                        placeholder="Subject"
                        required 
                        disabled={sending}
                    />
                </div>
                <div className="compose-form-row">
                    <textarea 
                        value={body} 
                        onChange={(e) => setBody(e.target.value)} 
                        rows="12" 
                        required 
                        disabled={sending}
                    ></textarea>
                </div>
                <div className="compose-actions">
                    <button type="submit" className="send-button" disabled={sending}>
                        {sending ? 'Sending...' : 'Send'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default Compose;
